import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { Button, Menu, Modal, Portal, Switch, Text, TextInput } from 'react-native-paper';
import { colors, spacing, typography } from '../../../constants/theme';
import { CATEGORY_ICONS } from '../adminCategoryIcons';
import { CategoryForm, ReviewCategory } from '../AdminDashboard.types';

interface AdminCreateCategoryModalProps {
  visible: boolean;
  form: CategoryForm;
  categories: ReviewCategory[];
  parentMenuVisible: boolean;
  selectedTemplateId: number | null;
  onChangeForm: (form: CategoryForm) => void;
  onChangeParentMenuVisible: (visible: boolean) => void;
  onDismiss: () => void;
  onSubmit: () => void;
}

export const AdminCreateCategoryModal: React.FC<AdminCreateCategoryModalProps> = ({
  visible,
  form,
  categories,
  parentMenuVisible,
  selectedTemplateId,
  onChangeForm,
  onChangeParentMenuVisible,
  onDismiss,
  onSubmit,
}) => (
  <Portal>
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      contentContainerStyle={styles.modal}
    >
      <ScrollView>
        <Text style={styles.modalTitle}>Create New Category</Text>

        <View style={styles.formContainer}>
          <TextInput
            label="Name *"
            value={form.name}
            onChangeText={(text) => onChangeForm({ ...form, name: text })}
            style={styles.formField}
          />

          <TextInput
            label="Description"
            value={form.description}
            onChangeText={(text) => onChangeForm({ ...form, description: text })}
            multiline
            style={styles.formField}
          />

          <Text>Parent Category (Optional)</Text>
          <Menu
            visible={parentMenuVisible}
            onDismiss={() => onChangeParentMenuVisible(false)}
            anchor={
              <Button
                mode="outlined"
                onPress={() => onChangeParentMenuVisible(true)}
                style={styles.formField}
              >
                {form.parentCategoryId
                  ? categories.find(c => c.id.toString() === form.parentCategoryId)?.name || 'Select Parent Category'
                  : 'No Parent (Top Level)'
                }
              </Button>
            }
          >
            <Menu.Item
              title="No Parent (Top Level)"
              onPress={() => {
                onChangeForm({ ...form, parentCategoryId: '' });
                onChangeParentMenuVisible(false);
              }}
            />
            {categories
              .filter(category => category.templateId === selectedTemplateId && !category.showInHamburger)
              .map(category => (
                <Menu.Item
                  key={category.id}
                  title={category.name}
                  onPress={() => {
                    onChangeForm({ ...form, parentCategoryId: category.id.toString() });
                    onChangeParentMenuVisible(false);
                  }}
                />
              ))}
          </Menu>

          <Text>Icon</Text>
          <View style={styles.iconGrid}>
            {CATEGORY_ICONS.map(iconOption => (
              <Button
                key={iconOption.value}
                mode={form.icon === iconOption.value ? 'contained' : 'outlined'}
                onPress={() => onChangeForm({ ...form, icon: iconOption.value })}
                style={styles.iconButton}
              >
                {iconOption.label}
              </Button>
            ))}
          </View>
        </View>

        <View style={styles.disabledHamburgerSettings}>
          <Text style={styles.helperText}>
            ℹ️ Add at least one active item to enable hamburger menu
          </Text>
          <View style={styles.disabledSwitchRow}>
            <Switch
              value={false}
              disabled
            />
            <Text style={styles.disabledSwitchLabel}>
              Show in Hamburger Menu
            </Text>
          </View>
        </View>

        <View style={styles.formRow}>
          <Button
            mode="outlined"
            onPress={onDismiss}
            style={styles.formField}
          >
            Cancel
          </Button>
          <Button
            mode="contained"
            onPress={onSubmit}
            style={styles.formField}
          >
            Create
          </Button>
        </View>
      </ScrollView>
    </Modal>
  </Portal>
);

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.background,
    margin: spacing.lg,
    padding: spacing.lg,
    borderRadius: 8,
    maxHeight: '90%',
    minHeight: '70%',
  },
  modalTitle: {
    ...typography.h2,
    marginBottom: spacing.lg,
  },
  formContainer: {
    gap: spacing.md,
  },
  formField: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
    paddingHorizontal: spacing.sm,
  },
  iconGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  iconButton: {
    minWidth: 100,
    marginBottom: spacing.xs,
  },
  disabledHamburgerSettings: {
    marginTop: spacing.md,
  },
  helperText: {
    color: colors.textSecondary,
    fontSize: 12,
    marginBottom: spacing.sm,
  },
  disabledSwitchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    opacity: 0.5,
  },
  disabledSwitchLabel: {
    marginLeft: spacing.sm,
    color: colors.textSecondary,
  },
  formRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
});

export default AdminCreateCategoryModal;
